import { Chip, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import LinkIcon from '@mui/icons-material/Link'
import ForwardToInboxIcon from '@mui/icons-material/ForwardToInbox'
import DeleteIcon from '@mui/icons-material/DeleteOutline'
import { IDataTableColumn } from '../../../../../components/Tables/DataTable'
import DataTableSubcellTypography from '../../../../../components/Typographys/DataTableSubcellTypography'
import { dateTimeString } from '../../../../../core/functions/dateTime'
import {
  CommunityMemberStatus,
  TCommunityMember,
} from '../../../../../core/types/CommunityMember'
import CommunityMemberFunctions from './CommunityMemberFunctions'

export interface ICommunityMemberColumnsProps {
  onRenewClick: (member: TCommunityMember) => void
  renewingId: string | null
  onResendEmailClick: (member: TCommunityMember) => void
  resendingId: string | null
  onRemoveClick: (member: TCommunityMember) => void
  removingId: string | null
}

export const getCommunityMemberColumns = ({
  onRenewClick,
  renewingId,
  onResendEmailClick,
  resendingId,
  onRemoveClick,
  removingId,
}: ICommunityMemberColumnsProps): readonly IDataTableColumn<TCommunityMember>[] => [
  {
    id: 'customerName',
    label: 'Cliente',
    minWidth: 200,
    render(_id, _value, row) {
      return (
        <Stack direction="column">
          <span>{row.customerName}</span>
          <DataTableSubcellTypography>
            {row.customerEmail}
          </DataTableSubcellTypography>
        </Stack>
      )
    },
  },
  {
    id: 'customerPhone',
    label: 'Telefone',
    minWidth: 140,
    format: (value: string | null) => CommunityMemberFunctions.formatPhone(value),
  },
  {
    id: 'orderCodeId',
    label: 'Pedido',
    minWidth: 120,
  },
  {
    id: 'status',
    label: 'Status',
    minWidth: 160,
    render(_id, _value, row) {
      const label = CommunityMemberFunctions.getStatusLabel(
        row.hasJoinedCommunity,
        row.isInviteLinkExpired
      )
      const styles = CommunityMemberFunctions.getStatusChipStyles(
        row.hasJoinedCommunity,
        row.isInviteLinkExpired
      )
      return (
        <Chip
          label={label}
          size="small"
          sx={{
            backgroundColor: styles.backgroundColor,
            color: styles.color,
            fontWeight: 600,
            fontSize: '12px',
          }}
        />
      )
    },
  },
  {
    id: 'createdDate',
    label: 'Criado em',
    minWidth: 140,
    format: (value: string) => dateTimeString(new Date(value)),
  },
  {
    id: 'inviteLinkExpiresAt',
    label: 'Expira em',
    minWidth: 140,
    render(_id, _value, row) {
      return (
        <Typography fontSize="14px">
          {row.inviteLinkExpiresAt
            ? dateTimeString(new Date(row.inviteLinkExpiresAt))
            : '-'}
        </Typography>
      )
    },
  },
  {
    id: 'actions',
    label: 'Ação',
    minWidth: 130,
    align: 'center',
    render(_id, _value, row) {
      const alreadyJoined = row.hasJoinedCommunity
      const alreadyRemoved = row.status === CommunityMemberStatus.Removed
      const awaitingEntry = !row.hasJoinedCommunity && !row.isInviteLinkExpired
      const removeDisabled = alreadyRemoved || awaitingEntry
      return (
        <Stack direction="row" spacing={0.5} justifyContent="center">
          <Tooltip
            title={
              alreadyJoined
                ? 'O cliente já entrou no grupo'
                : 'Gerar novo link de convite'
            }
          >
            <span>
              <IconButton
                size="small"
                disabled={alreadyJoined || renewingId === row.id}
                onClick={() => onRenewClick(row)}
              >
                <LinkIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip
            title={
              alreadyJoined
                ? 'O cliente já entrou no grupo'
                : 'Reenviar link por e-mail ao cliente'
            }
          >
            <span>
              <IconButton
                size="small"
                disabled={alreadyJoined || resendingId === row.id}
                onClick={() => onResendEmailClick(row)}
              >
                <ForwardToInboxIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip
            title={
              alreadyRemoved
                ? 'Cliente já foi removido'
                : awaitingEntry
                  ? 'Cliente ainda não entrou no grupo'
                  : 'Remover cliente do grupo'
            }
          >
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={removeDisabled || removingId === row.id}
                onClick={() => onRemoveClick(row)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      )
    },
  },
]
